import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import CustomSelect from '../../../common/formElements/CustomSelect';
import ParentCardNoCol from '../../common/ParentCardNoCol';
import { MappingForm } from './MappingForm';
import { getFilteredArray } from '../../../common/utils/rest_utils';
import { getFieldInfoByName } from '../../events/action/collection/Util';
import FormFieldWithLabel from '../../common/FormFieldWithLabel';
import { useDispatch } from 'react-redux';
import { loadExternalApis } from '../../external-api/redux/loadExternalApis';
import { REST_API } from 'drapcode-constant';
import CustomRadio from '../../../common/ui/CustomRadio';
import Col6FormCheck from '../../../common/ui/Col6FormCheck';

export const CustomDataMappingForm = ({
  projectId,
  collections,
  externalApis,
  projectDetail,
  dataMappingId,
  customDataMapping,
  history,
  actions,
  version,
}) => {
  const dispatch = useDispatch();
  const options = [
    {
      value: 'COLLECTION',
      label: 'Collection',
    },
    {
      value: 'EXTERNAL_API',
      label: 'External API',
    },
  ];

  const collectionOptions = collections.map((coll) => ({
    value: coll.collectionName,
    label: coll.collectionTitle,
  }));

  const categorizeExternalApis = (apis) => {
    const categorized = apis.reduce((acc, api) => {
      const category =
        api.externalApiType === REST_API ? 'REST API' : api.externalApiType || 'REST API';
      if (!acc[category]) acc[category] = [];
      acc[category].push({ value: api.uuid, label: api.externalApiName });
      return acc;
    }, {});
    return Object.entries(categorized).map(([label, options]) => ({ label, options }));
  };
  const externalApisOptions = categorizeExternalApis(externalApis);

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [filter, setFilter] = useState('');
  const [externalApi, setExternalApi] = useState('');
  const [responsePath, setResponsePath] = useState('');
  const [mapping, setMapping] = useState([{ id: 0, key: '', value: '' }]);
  const [decrypted, setDecrypted] = useState(false);
  const [collection, setCollection] = useState(null);
  const [refArrayFormat, setRefArrayFormat] = useState(false);

  useEffect(() => {
    dispatch(loadExternalApis({ projectId, version }));
    if (customDataMapping) {
      setName(customDataMapping.name);
      setType(customDataMapping.type);
      handleCollectionChange(customDataMapping.collectionName);
      setFilter(customDataMapping.filter);
      setExternalApi(customDataMapping.externalApi);
      setResponsePath(customDataMapping.responsePath);
      setMapping([
        ...customDataMapping.mapping,
        { id: customDataMapping.mapping.length, key: '', value: '' },
      ]);
      setDecrypted(customDataMapping.decrypted);
      setRefArrayFormat(customDataMapping.refArrayFormat);
    }
  }, [customDataMapping, dispatch]);

  const handleCollectionChange = (collectionName) => {
    const collection = collections.find((coll) => coll.collectionName === collectionName);
    setCollectionName(collectionName);
    setCollection(collection);
  };

  const getFilterOptions = () => {
    let options = [];
    if (!collectionName) return options;
    const { finders } = collection || [];
    if (finders) {
      finders.forEach((filter) => {
        options.push({ value: filter.uuid, label: filter.name });
      });
    }
    return options;
  };

  const getCurrentCollectionName = () => {
    let selectedCollection = '';
    if (type === 'COLLECTION' && collectionName) {
      selectedCollection = collectionName;
    } else if (type === 'EXTERNAL_API' && externalApi) {
      const restApi = externalApis.find((api) => api.uuid === externalApi);
      const { responseDataMapping } = restApi;
      const { selectedCollectionName } = responseDataMapping;
      selectedCollection = selectedCollectionName;
    }
    return selectedCollection;
  };

  const handleSubmit = (values) => {
    const mappingData = getFilteredArray(mapping);
    if (dataMappingId) {
      actions
        .updateCustomDataMapping({
          uuid: dataMappingId,
          projectId,
          name: values.name,
          type,
          collectionName,
          filter,
          externalApi,
          responsePath: values.responsePath,
          mapping: mappingData,
          decrypted: values.decrypted,
          refArrayFormat: values.refArrayFormat,
        })
        .then(() => history.goBack());
    } else {
      actions
        .addCustomDataMapping({
          projectId,
          name: values.name,
          type,
          collectionName,
          filter,
          externalApi,
          responsePath: values.responsePath,
          mapping: mappingData,
          decrypted: values.decrypted,
          refArrayFormat: values.refArrayFormat,
        })
        .then(() => history.goBack());
    }
  };

  const isDecryption = () => {
    if (!projectDetail) return false;
    if (!collection) return false;
    const encryption = projectDetail && projectDetail.enableEncryption;
    const encryptedField = collection.fields.find((field) => field.encrypted);
    return encryption && encryptedField;
  };

  return (
    <ParentCardNoCol
      title={`${dataMappingId ? 'Update' : 'Add'} Custom Data Mapping`}
      icon="customDataMapping"
      bgColor="drapcode-violet"
    >
      <Formik
        enableReinitialize
        initialValues={{ name, responsePath, decrypted, refArrayFormat }}
        onSubmit={(values, { resetForm }) => {
          handleSubmit(values);
          resetForm();
        }}
      >
        {({ isSubmitting, setFieldValue, setFieldTouched, resetForm, errors }) => {
          return (
            <Form className="form-horizontal">
              <div className="row mx-auto">
                <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Name"
                      icon="text"
                      infoIcon={true}
                      infoDescription={getFieldInfoByName('name')}
                    >
                      <Field
                        type="text"
                        name="name"
                        placeholder="Name"
                        className="form-control form-control-sm"
                      />
                    </FormFieldWithLabel>
                  </div>
                </div>
                <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Data Source"
                      icon="select"
                      infoIcon={true}
                      infoDescription={getFieldInfoByName('type')}
                    >
                      <CustomSelect
                        name={'type'}
                        isMulti={false}
                        value={type}
                        options={options}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        onChangeValue={(name, value) => setType(value)}
                      />
                    </FormFieldWithLabel>
                  </div>
                </div>
                {type && type === 'COLLECTION' && (
                  <>
                    <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                      <div className="form-group">
                        <FormFieldWithLabel
                          cta={false}
                          label="Collection"
                          icon="select"
                          infoIcon={true}
                          infoDescription={getFieldInfoByName('collectionName')}
                        >
                          <CustomSelect
                            name={'collectionName'}
                            isMulti={false}
                            value={collectionName}
                            options={collectionOptions}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            onChangeValue={(name, value) => handleCollectionChange(value)}
                          />
                        </FormFieldWithLabel>
                      </div>
                    </div>
                    {collectionName && (
                      <>
                        <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                          <div className="form-group">
                            <FormFieldWithLabel
                              cta={false}
                              label="Filter"
                              icon="select"
                              infoIcon={true}
                              infoDescription={getFieldInfoByName('filter')}
                            >
                              <CustomSelect
                                name={'filter'}
                                isMulti={false}
                                value={filter}
                                options={getFilterOptions()}
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                                onChangeValue={(name, value) => setFilter(value)}
                              />
                            </FormFieldWithLabel>
                          </div>
                        </div>
                        {isDecryption() && (
                          <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                            <div className="form-group">
                              <Col6FormCheck>
                                <CustomRadio label="Decrypted" isRadio={false}>
                                  <Field
                                    type="checkbox"
                                    name="decrypted"
                                    className="form-check-input"
                                  />
                                </CustomRadio>
                                <small>( Decrypt the Data.)</small>
                                <p className="errors">
                                  <ErrorMessage name="decrypted" />
                                </p>
                              </Col6FormCheck>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                {type && type === 'EXTERNAL_API' && (
                  <>
                    <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                      <div className="form-group">
                        <FormFieldWithLabel
                          cta={false}
                          label="External API"
                          icon="select"
                          infoIcon={true}
                          infoDescription={getFieldInfoByName('externalApi')}
                        >
                          <CustomSelect
                            name={'externalApi'}
                            isMulti={false}
                            value={externalApi}
                            options={externalApisOptions}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            onChangeValue={(name, value) => setExternalApi(value)}
                          />
                        </FormFieldWithLabel>
                      </div>
                    </div>
                  </>
                )}
                <div className="col-12">
                  <br />
                  <hr />
                  <br />
                  <h4 className="card-title px-3">
                    Data Mapping
                    <br />
                  </h4>
                </div>
                <div className="col-md-6 col-lg-6 col-sm-6 col-xs-6">
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Response JSON Path"
                      icon="text"
                      infoIcon={true}
                      infoDescription={getFieldInfoByName('responsePath')}
                    >
                      <Field
                        type="text"
                        name="responsePath"
                        placeholder="Response JSON Path"
                        className="form-control form-control-sm"
                      />
                    </FormFieldWithLabel>
                  </div>
                </div>
                <Col6FormCheck>
                  <CustomRadio label="Reference Values as List" isRadio={false}>
                    <Field type="checkbox" name="refArrayFormat" className="form-check-input" />
                  </CustomRadio>
                  <small>( Parse reference field values in Array format. )</small>
                  <p className="errors">
                    <ErrorMessage name="refArrayFormat" />
                  </p>
                </Col6FormCheck>
                <div className="col-12 px-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                        <MappingForm
                          mapping={mapping}
                          setMapping={setMapping}
                          collectionName={getCurrentCollectionName()}
                          collections={collections}
                          projectDetail={projectDetail}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-lg-12 mt-2 text-right">
                  <button
                    type="button"
                    className="btn btn-default btn-md"
                    onClick={() => {
                      resetForm();
                      history.goBack();
                    }}
                  >
                    <span>
                      <i className={`wb ${drapcodeIcon('cancel')}`}></i>&nbsp;Cancel
                    </span>
                  </button>
                  &nbsp;
                  <button type="submit" className="btn btn-theme-submit btn-md">
                    <i className={`wb ${drapcodeIcon('save')}`}></i>&nbsp;Save&nbsp;
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ParentCardNoCol>
  );
};
